import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { useSessionStore } from '@/store/sessionStore';
import { fetchProgramCount } from '@/api/achPrograms';
import type { PortalAppConfig } from '@/config/runtimeConfig';
import home from './Home.module.css';
import styles from './AppCard.module.css';

/**
 * EAA-1701 (A5.3): one launcher card = one app from the registry.
 *
 * Each card is its OWN query (never a cross-system `Promise.all`), so a slow or
 * down app degrades only its own card while the rest of the launcher renders —
 * the "fail-soft per card" contract. The Content Studio (ach) card shows a LIVE
 * program count fetched with the RS256 token; other apps are plain deep links.
 */
export function AppCard({ code, app }: { code: string; app: PortalAppConfig }) {
    const { t } = useTranslation();
    const { accessToken } = useAuth();
    const activeTenantId = useSessionStore((state) => state.activeTenantId);

    // Only the Content Studio card carries a live metric in Phase A (P1 read).
    const isContentBank = code === 'ach';

    const countQuery = useQuery({
        queryKey: ['program-count', code, accessToken, activeTenantId],
        queryFn: () => fetchProgramCount(app.apiBase, accessToken as string, activeTenantId),
        enabled: isContentBank && Boolean(accessToken),
        retry: false,
        staleTime: 30_000, // brief SWR cache to blunt re-fetch on tenant flips (R3)
    });

    return (
        <a className={home.launcherCard} href={app.webUrl}>
            <span className={home.launcherCardIcon} aria-hidden="true">
                <AppIcon />
            </span>
            <strong className={home.launcherCardTitle}>{app.name}</strong>
            {isContentBank && (
                <span className={styles.metric}>
                    {countQuery.isLoading && (
                        <em aria-busy="true">{t('home.launcher.appCard.counting')}</em>
                    )}
                    {/* Degrade this card only — never surface as a launcher-wide error. */}
                    {countQuery.isError && (
                        <em className={styles.metricError}>
                            {t('home.launcher.appCard.unavailable')}
                        </em>
                    )}
                    {countQuery.isSuccess &&
                        t('home.launcher.appCard.programCount', { count: countQuery.data })}
                </span>
            )}
        </a>
    );
}

function AppIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path d="M14 3v4a1 1 0 0 0 1 1h4" strokeLinecap="round" strokeLinejoin="round" />
            <path
                d="M5 3h9l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
                strokeLinejoin="round"
            />
        </svg>
    );
}
